import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, Clock, ArrowRight, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const PAGE_SIZE = 9;

function readingTime(content) {
  const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function BlogListPage() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const page = Math.max(parseInt(params.get("page") || "1", 10), 1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/blog/posts", {
        params: { page, page_size: PAGE_SIZE, ...(category ? { category } : {}) },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, category]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Blog — Eazy-Travels"; }, []);

  useEffect(() => {
    api.get("/blog/posts", { params: { page: 1, page_size: 200 } })
      .then(({ data }) => {
        const set = new Set((data.items || []).map((p) => p.category).filter(Boolean));
        setCategories(Array.from(set));
      })
      .catch(() => {});
  }, []);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  function setCategory(next) {
    const p = new URLSearchParams(params);
    if (next) p.set("category", next); else p.delete("category");
    p.delete("page");
    setParams(p);
  }

  function setPage(next) {
    const p = new URLSearchParams(params);
    p.set("page", String(next));
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)]">
        <section className="pt-28 pb-16 bg-radial-gold border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="overline mb-3">{t("nav.blog")}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
              Insights & <span className="gold-text">Guides</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed max-w-3xl mx-auto">
              Visa updates, relocation tips, and expert advice from our advisors — straight from the field.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center mb-12" data-testid="blog-category-filters">
                <button
                  onClick={() => setCategory("")}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition ${
                    !category ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 text-white/60 hover:border-white/30"
                  }`}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`text-xs px-3.5 py-1.5 rounded-full border transition ${
                      category === c ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                    data-testid={`blog-category-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="text-center py-20"><Loader2 size={28} className="text-[var(--gold)] animate-spin mx-auto" /></div>
            )}

            {!loading && items.length === 0 && (
              <div className="text-center py-20 text-white/55" data-testid="blog-empty">
                No posts published yet. Check back soon.
              </div>
            )}

            {!loading && items.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((post, i) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="card-dark overflow-hidden group fade-up flex flex-col"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="h-48 overflow-hidden bg-gradient-to-br from-[#ffb870]/20 to-[#1e90d5]/15">
                      {post.featured_image && (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        />
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      {post.category && <span className="overline mb-2">{post.category}</span>}
                      <h2 className="font-display text-xl font-bold text-white mb-2 leading-snug group-hover:text-[var(--gold)] transition">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="text-white/60 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between text-xs text-white/40 pt-2">
                        <span className="flex items-center gap-1.5">
                          <CalendarDays size={13} /> {new Date(post.published_at || post.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={13} /> {readingTime(post.content)} min read
                        </span>
                      </div>
                      <span className="mt-3 text-sm text-[var(--gold)] inline-flex items-center gap-1.5">
                        Read more <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-14" data-testid="blog-pagination">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-full text-sm border transition ${
                      p === page ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
